import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchRestaurantFooter } from "../../actions/footerActions";
import "./css/Footer.css";

export default function Footer() {
  const { restaurantId, restaurantSlug } = useParams();
  const restaurant = restaurantSlug || restaurantId;
  const dispatch = useDispatch();
  const footer = useSelector((state) => state.footer?.data);
  const footerError = useSelector((state) => state.footer?.error);

  useEffect(() => {
    if (restaurant) {
      dispatch(fetchRestaurantFooter(restaurant)).catch(() => {});
    }
  }, [dispatch, restaurant]);

  // Render only social profiles configured in Restaurant Admin -> Settings.
  const socialLinks = [
    { key: "facebook_url", label: "Facebook", symbol: "f" },
    { key: "instagram_url", label: "Instagram", symbol: "◎" },
    { key: "youtube_url", label: "YouTube", symbol: "▶" },
    { key: "whatsapp_url", label: "WhatsApp", symbol: "◉" },
  ].filter(({ key }) => typeof footer?.[key] === "string" && /^https?:\/\//i.test(footer[key].trim()));

  return (
    <footer className="restaurant-footer" id="contact">
      <div className="footer-container">
        <div className="footer-brand">
          <div className="footer-logo">
            <div className="footer-logo-circle">♧</div>
            <div>
              <div className="footer-logo-name">{footer?.restaurant_name || ""}</div>
              <div className="footer-logo-subtitle">RESTAURANT</div>
            </div>
          </div>
          <p className="footer-description">{footer?.description || ""}</p>
          {socialLinks.length > 0 && (
            <div className="footer-social">
              {socialLinks.map(({ key, label, symbol }) => (
                <a
                  key={key}
                  href={footer[key].trim()}
                  aria-label={label}
                  title={label}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {symbol}
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="footer-column">
          <h3>Quick Links</h3>
          <a href="#home">Home</a>
          <a href="#menu">Menu</a>
          <a href="#about">About</a>
          <a href="#gallery">Gallery</a>
          <a href="#contact">Contact</a>
        </div>

        <div className="footer-column">
          <h3>Opening Hours</h3>
          <div className="opening-row">
            <span>Mon - Fri</span>
            <span>{footer?.monday_friday_hours || "—"}</span>
          </div>
          <div className="opening-row">
            <span>Saturday</span>
            <span>{footer?.saturday_hours || "—"}</span>
          </div>
          <div className="opening-row">
            <span>Sunday</span>
            <span>{footer?.sunday_hours || "—"}</span>
          </div>
        </div>

        <div className="footer-column">
          <h3>Contact Us</h3>
          <div className="contact-item">
            <span className="contact-icon">●</span>
            <span>{footer?.address || ""}</span>
          </div>
          <div className="contact-item">
            <span className="contact-icon">☎</span>
            <span>{footer?.phone || ""}</span>
          </div>
          <div className="contact-item">
            <span className="contact-icon">✉</span>
            <span>{footer?.email || ""}</span>
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <p>© {new Date().getFullYear()} {footer?.restaurant_name || ""}. All rights reserved.</p>
        <div>
          {footer?.privacy_policy_url && (
            <a href={footer.privacy_policy_url} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          )}
          {footer?.terms_url && (
            <a href={footer.terms_url} target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</a>
          )}
        </div>
      </div>
      <p className="footer-disclaimer" style={{ textAlign: "center", fontSize: "0.75rem", opacity: 0.8, margin: "0.75rem 1rem" }}>
        {footer?.disclaimer || "Food images are for presentation purposes only. Actual items may vary."}
      </p>
      {process.env.NODE_ENV === "development" && footerError && (
        <p role="alert" style={{ textAlign: "center", fontSize: "0.75rem" }}>
          Unable to load footer: {footerError}
        </p>
      )}
    </footer>
  );
}